<template>
  <div class="footer-area pt-100 pb-75">
    <div class="container mw-1356">
      <div class="row">

        <!-- Brand column -->
        <div class="col-lg-4" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="100">
          <div class="footer-single-widget mw-250">
            <RouterLink to="/" class="d-inline-block footer-logo">
              <img :src="footer.logo_url || defaultLogo" alt="logo" />
            </RouterLink>
            <p>{{ footer.description }}</p>
            <h4>Social Media</h4>
            <ul class="ps-0 mb-0 list-unstyled d-flex social-links">
              <li v-if="footer.facebook_url">
                <a :href="footer.facebook_url" target="_blank" rel="noopener"><i class="ri-facebook-fill"></i></a>
              </li>
              <li v-if="footer.twitter_url">
                <a :href="footer.twitter_url" target="_blank" rel="noopener"><i class="ri-twitter-x-fill"></i></a>
              </li>
              <li v-if="footer.instagram_url">
                <a :href="footer.instagram_url" target="_blank" rel="noopener"><i class="ri-instagram-fill"></i></a>
              </li>
              <li v-if="footer.linkedin_url">
                <a :href="footer.linkedin_url" target="_blank" rel="noopener"><i class="ri-linkedin-fill"></i></a>
              </li>
            </ul>
          </div>
        </div>

        <div class="col-lg-8">
          <div class="row">

            <!-- Services links -->
            <div class="col-xl-3 col-sm-6" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="200">
              <div class="footer-single-widget">
                <h3>Services</h3>
                <ul class="ps-0 mb-0 list-unstyled import-links">
                  <li v-for="link in footer.service_links" :key="link.url">
                    <RouterLink :to="link.url">{{ link.text }}</RouterLink>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Company links -->
            <div class="col-xl-3 col-sm-6" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="300">
              <div class="footer-single-widget">
                <h3>Company</h3>
                <ul class="ps-0 mb-0 list-unstyled import-links">
                  <li v-for="link in footer.company_links" :key="link.url">
                    <RouterLink :to="link.url">{{ link.text }}</RouterLink>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Contact -->
            <div class="col-xl-3 col-sm-6" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="400">
              <div class="footer-single-widget">
                <h3>Get In Touch</h3>
                <ul class="ps-0 mb-0 list-unstyled contact-links">
                  <li v-for="c in footer.contact" :key="c.phone">
                    <a :href="'tel:' + c.phone">{{ c.phone }}</a>
                    <p class="mb-0">{{ c.note }}</p>
                  </li>
                </ul>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Payment / compliance strip -->
      <div class="footer-compliance">
        <div class="fc-pay">
          <CardMarks />
          <span class="fc-secure"><i class="ti ti-lock"></i> Secure checkout · Prices in {{ company.payments.currency }}</span>
        </div>
        <div class="fc-legal">
          <RouterLink to="/terms-conditions">Terms</RouterLink>
          <RouterLink to="/refund-policy">Refunds</RouterLink>
          <RouterLink to="/privacy-policy">Privacy</RouterLink>
        </div>
        <p v-if="address" class="fc-addr">
          {{ company.legalName }} · {{ address }}
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { RouterLink } from "vue-router";
import { useContent } from "@/composables/useContent";
import CardMarks from "@/components/Common/CardMarks.vue";
import { company, establishmentLine } from "@/config/company";

const footerDefaults = {
  logo_url: "",
  description: "Helping our communities connect, work, & thrive. Premium IPTV streaming with 25,000+ channels worldwide.",
  facebook_url:  "https://www.facebook.com/",
  twitter_url:   "https://www.twitter.com/",
  instagram_url: "https://www.instagram.com/",
  linkedin_url:  "https://www.linkedin.com/",
  contact: [
    { phone: "+1 (800) 216 20 20", note: "(for customer service and support)" },
    { phone: "+1 (800) 216 20 21", note: "(for new customers)" },
  ],
  service_links: [
    { text: "TV Channels",        url: "/channels" },
    { text: "World Cup 2026",     url: "/world-cup-2026" },
    { text: "Pricing Plans",      url: "/pricing" },
    { text: "Installation Guide", url: "/installation-guide" },
  ],
  company_links: [
    { text: "About Us",       url: "/about-us" },
    { text: "Blog",           url: "/blog" },
    { text: "FAQ",            url: "/faq" },
    { text: "Contact Us",     url: "/contact-us" },
    { text: "Privacy Policy", url: "/privacy-policy" },
  ],
};

export default defineComponent({
  name: "MainFooter",
  components: { CardMarks, RouterLink },
  setup() {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const defaultLogo = require("@/assets/images/white-logo.svg");
    const { content: footer } = useContent("footer", footerDefaults);
    return { footer, defaultLogo, company, address: establishmentLine() };
  },
});
</script>

<style scoped>
.footer-compliance {
  margin-top: 2.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem 1.5rem;
}
.fc-pay {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  flex-wrap: wrap;
}
.fc-secure {
  font-size: 0.8rem;
  color: #b9b9b9;
}
.fc-secure i {
  color: #4ade80;
  margin-right: 2px;
}
.fc-legal {
  display: flex;
  gap: 1.1rem;
  margin-left: auto;
}
.fc-legal a {
  color: #b9b9b9;
  font-size: 0.82rem;
  text-decoration: none;
}
.fc-legal a:hover {
  color: #fff;
}
.fc-addr {
  flex-basis: 100%;
  margin: 0;
  font-size: 0.76rem;
  color: #8a8a8a;
}
@media (max-width: 600px) {
  .fc-legal { margin-left: 0; }
}
</style>
